<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  findComponentByType,
  COMPONENT_TYPES,
} from 'dashboard/helper/templateHelper';
import WhatsAppBubblePreview from 'dashboard/components-next/whatsapp/WhatsAppBubblePreview.vue';

const props = defineProps({
  wizardData: { type: Object, required: true },
});

const { t } = useI18n();

const isLabel = computed(() => props.wizardData.audienceType === 'label');
const isCsv = computed(() => props.wizardData.audienceType === 'csv_import');

const templatePreview = computed(() => {
  if (!props.wizardData.selectedTemplate) return '';

  const template = props.wizardData.selectedTemplate;
  const bodyComp = template.components?.find(c => c.type === 'BODY');
  let text = bodyComp?.text || '';

  if (isCsv.value && props.wizardData.csvSampleRows?.length > 0) {
    const sampleRow = props.wizardData.csvSampleRows[0];
    const varMapping = props.wizardData.columnMapping?.variable_mapping || {};

    Object.entries(varMapping).forEach(([varKey, varConfig]) => {
      let replacement = '';
      if (typeof varConfig === 'string') {
        replacement = sampleRow[varConfig] || '';
      } else if (varConfig?.type === 'static') {
        replacement = varConfig.value || '';
      } else if (varConfig?.type === 'column') {
        replacement = sampleRow[varConfig.value] || '';
      }
      if (replacement) {
        text = text.replace(`{{${varKey}}}`, replacement);
      }
    });
  } else if (isLabel.value && props.wizardData.processedParams?.body) {
    const bodyParams = props.wizardData.processedParams.body;
    Object.entries(bodyParams).forEach(([varKey, value]) => {
      if (value) {
        text = text.replace(`{{${varKey}}}`, value);
      }
    });
  }

  return text;
});

const templateHeader = computed(() =>
  findComponentByType(props.wizardData.selectedTemplate, COMPONENT_TYPES.HEADER)
);

const templateFooter = computed(() =>
  findComponentByType(props.wizardData.selectedTemplate, COMPONENT_TYPES.FOOTER)
);

const templateButtons = computed(() => {
  const comp = findComponentByType(
    props.wizardData.selectedTemplate,
    COMPONENT_TYPES.BUTTONS
  );
  return comp?.buttons || [];
});

const audienceSummary = computed(() => {
  if (isLabel.value) {
    return t('CAMPAIGN.WHATSAPP.WIZARD.PREVIEW.LABELS_AUDIENCE', {
      count: props.wizardData.selectedAudience.length,
    });
  }
  return t('CAMPAIGN.WHATSAPP.WIZARD.PREVIEW.CSV_AUDIENCE', {
    count: props.wizardData.csvTotalRows,
  });
});

const mappingSummary = computed(() => {
  if (!isCsv.value) return [];
  const mapping = props.wizardData.columnMapping || {};
  const items = [];

  if (mapping.phone_column)
    items.push({
      label: t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.PHONE_COLUMN'),
      value: mapping.phone_column,
    });
  if (mapping.name_column)
    items.push({
      label: t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.NAME_COLUMN'),
      value: mapping.name_column,
    });
  if (mapping.email_column)
    items.push({
      label: t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.EMAIL_COLUMN'),
      value: mapping.email_column,
    });

  const varMapping = mapping.variable_mapping || {};
  Object.entries(varMapping).forEach(([varKey, varConfig]) => {
    if (typeof varConfig === 'string') {
      if (varConfig) items.push({ label: `{{${varKey}}}`, value: varConfig });
    } else if (varConfig?.type === 'static') {
      items.push({
        label: `{{${varKey}}}`,
        value: `"${varConfig.value}"`,
      });
    } else if (varConfig?.type === 'column' && varConfig.value) {
      items.push({ label: `{{${varKey}}}`, value: varConfig.value });
    }
  });

  return items;
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <h3 class="text-base font-medium text-n-slate-12">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.PREVIEW.TITLE') }}
    </h3>

    <div class="grid grid-cols-2 gap-4">
      <!-- Left: Summary -->
      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ wizardData.title }}
          </span>
        </div>

        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ wizardData.selectedTemplate?.name || '-' }}
          </span>
        </div>

        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.TITLE') }}
          </span>
          <span class="text-sm text-n-slate-12">
            {{ audienceSummary }}
          </span>
        </div>

        <!-- CSV Mapping summary -->
        <div v-if="mappingSummary.length > 0" class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.WIZARD.MAPPING.TITLE') }}
          </span>
          <div class="flex flex-col gap-1">
            <div
              v-for="item in mappingSummary"
              :key="item.label"
              class="flex items-center gap-2 text-xs"
            >
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              <span class="text-n-slate-10">{{ item.label }}:</span>
              <code class="px-1 py-0.5 rounded bg-n-alpha-2 text-n-slate-12">
                {{ item.value }}
              </code>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Message preview -->
      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.PREVIEW.MESSAGE') }}
        </span>
        <WhatsAppBubblePreview
          :header-type="templateHeader?.format || ''"
          :header-text="templateHeader?.text || ''"
          :body-text="templatePreview || wizardData.renderedTemplate || ''"
          :footer-text="templateFooter?.text || ''"
          :buttons="templateButtons"
        />
        <p
          v-if="isCsv && wizardData.csvSampleRows?.length > 0"
          class="text-xs text-n-slate-10 italic"
        >
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.PREVIEW.SAMPLE_NOTE') }}
        </p>
      </div>
    </div>
  </div>
</template>
